import React from 'react';
import { useTranslation } from 'react-i18next';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowUp, Copy, Eye, Lock, Trash2 } from 'lucide-react';
import { DataTable } from '@/components/data-table';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Link } from '@tanstack/react-router';
const columnHelper = legacyCreateColumnHelper();
export const CampaignDataTable = ({ data, canClone, onView, onDelete, onChangeStatus, isUpdatingStatus, isDeleting, dateFormat, }) => {
    const [rowSelection, setRowSelection] = React.useState({});
    const { t } = useTranslation();
    const columns = React.useMemo(() => [
        columnHelper.accessor('publicId', {
            id: 'view',
            header: () => t('view'),
            cell: (info) => (<Button variant="ghost" size="icon" onClick={() => {
                    onView(info.getValue());
                }}>
                        <Eye />
                    </Button>),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('imageUrl', {
            header: () => t('image'),
            cell: ({ row: { original: { name, imageThumbnailUrl, imageUrl }, }, }) => {
                // Prefer the 64×64 Liip variant so the browser downloads
                // ~5KB instead of the full 1600×1600 original (~hundreds
                // of KB). Fall back to the original for legacy uploads
                // before Liip was wired up.
                const src = imageThumbnailUrl ?? imageUrl;
                return (<div className="w-16 h-16 rounded-xl overflow-hidden bg-muted flex items-center justify-center">
                            {src ? (<img src={src} alt={name} className="object-cover w-full h-full" loading="lazy" width={64} height={64}/>) : (<span className="text-muted-foreground text-xs">AD</span>)}
                        </div>);
            },
            footer: () => t('image'),
        }),
        columnHelper.accessor('name', {
            header: ({ column }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                        {t('campaign_name')}
                        {column.getIsSorted() === 'asc' ? <ArrowUp /> : <ArrowDown />}
                    </Button>),
            cell: (info) => info.renderValue(),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('totalScans', {
            header: ({ column }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                        {t('total_scans')}
                        {column.getIsSorted() === 'asc' ? <ArrowUp /> : <ArrowDown />}
                    </Button>),
            cell: (info) => info.renderValue(),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('createdAt', {
            header: ({ column }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                        {t('created_at')}
                        {column.getIsSorted() === 'asc' ? <ArrowUp /> : <ArrowDown />}
                    </Button>),
            cell: (info) => format(info.getValue(), dateFormat),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('updatedAt', {
            header: ({ column }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                        {t('last_updated')}
                        {column.getIsSorted() === 'asc' ? <ArrowUp /> : <ArrowDown />}
                    </Button>),
            cell: (info) => format(info.getValue(), dateFormat),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('status', {
            header: ({ column }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                        {t('status')}
                        {column.getIsSorted() === 'asc' ? <ArrowUp /> : <ArrowDown />}
                    </Button>),
            cell: ({ row: { original: { status, publicId }, }, }) => (<div className="flex gap-2">
                        {(status === 'draft' || status === 'archived') && (<Button size="sm" onClick={() => onChangeStatus(publicId, 'active')} disabled={isUpdatingStatus}>
                                {t('campaigns.actions.activate', {
                        ns: 'glossary',
                        defaultValue: 'Activate',
                    })}
                            </Button>)}

                        {status === 'active' && (<Button size="sm" variant="outline" onClick={() => onChangeStatus(publicId, 'archived')} disabled={isUpdatingStatus}>
                                {t('campaigns.actions.archive', {
                        ns: 'glossary',
                        defaultValue: 'Archive',
                    })}
                            </Button>)}
                    </div>),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor('publicId', {
            id: 'actions',
            enableHiding: false,
            header: () => t('actions'),
            cell: ({ getValue }) => {
                const publicId = getValue();
                const buttons = [];
                if (canClone) {
                    buttons.push(<Tooltip key={`clone-${publicId}`}>
                                <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" asChild title={t('clone')}>
                                        <Link to="/console/campaigns/new" search={{ cloneFrom: publicId }}>
                                            <Copy />
                                        </Link>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('clone')}</TooltipContent>
                            </Tooltip>);
                }
                else {
                    buttons.push(<Tooltip key={`clone-${publicId}`}>
                                <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                        <Lock />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('features.pro_only.clone', { ns: 'glossary' })}</TooltipContent>
                            </Tooltip>);
                }
                buttons.push(<Button variant="ghost" className="text-red-500" disabled={isDeleting} key={`delete-${publicId}`} onClick={() => {
                        onDelete(getValue());
                    }}>
                            <Trash2 />
                        </Button>);
                return buttons;
            },
        }),
    ], [canClone, dateFormat, isDeleting, isUpdatingStatus, onChangeStatus, onDelete, onView, t]);
    return (<DataTable data={data} columns={columns} onRowSelectionChange={setRowSelection} rowSelection={rowSelection}/>);
};
CampaignDataTable.displayName = 'CampaignDataTable';
